import { DigestReviewPanel } from '@/components/digest/digest-review-panel';
import { PaliersEditor } from '@/components/paliers-editor';
import { ContextFilesField } from '@/components/wizard/context-files-field';
import { SetupStepRow } from '@/components/wizard/setup-step-row';
import { matricesApi } from '@/lib/api';
import { matrixDisplayState } from '@/lib/matrix-grid-search';
import { useDeriveMatrix } from '@/lib/use-derive-matrix';
import { cn } from '@/lib/utils';
import type {
  CompanyWithSetup,
  MatrixPivot,
  MatrixWithDimensions,
  ResolvedPalier,
  UserRole,
} from '@mss_creator/shared';
import { Alert, Button, Icon, TextArea } from '@pms-digital/design-system';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { CadrageFrozenChoices } from './cadrage-frozen-choices';
import { CadragePromptSection } from './cadrage-prompt-section';
import { sectionIdFromHash, visibleCadrageSections } from './cadrage-view';
import { useSectionSpy } from './use-section-spy';

/**
 * The Cadrage tab (issue #3) — everything that feeds a generation, on one
 * full-width surface.
 *
 * It replaces a genuinely scattered arrangement: the singularity setup on the
 * company sheet, the sujet and contexte in a 384px drawer tab, the paliers
 * nowhere at matrix level, and the prompt nowhere at all once the matrix had
 * been generated. Those pieces are read against each other — a palier
 * description only makes sense beside the setup it grades — so the surface is
 * one scroller with a sticky rail, not six sub-tabs that would reproduce the
 * same fragmentation at shorter range.
 *
 * Two of the six sections are read-only by decision, not by omission. The
 * setup and the company contexte belong to the company and are shared by every
 * one of its matrices; editing them from inside one matrix would quietly
 * rewrite the others. Each therefore links to the company sheet instead. This
 * is the rule `ContexteTab` already applied to the company contexte alone,
 * generalised.
 *
 * The grid is NOT unmounted while this is open — see the route. Zoom,
 * selection and scroll position survive a round trip.
 */

export interface CadragePanelProps {
  matrix: MatrixWithDimensions;
  company: CompanyWithSetup;
  /** Effective role (settings-page override included) — gates the Prompt section's entry point only; the backend gates the data. */
  role: UserRole;
}

const CONFLICT_MESSAGE =
  'Une génération est en cours sur cette matrice — le cadrage ne peut pas être modifié tant qu’elle tourne.';

function saveErrorMessage(error: unknown): string {
  const status = (error as { status?: number } | null)?.status;
  if (status === 409) return CONFLICT_MESSAGE;
  return 'L’enregistrement a échoué. Réessayez dans un instant.';
}

function formatDate(isoDate: string): string {
  return new Date(isoDate).toLocaleDateString('fr-FR', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
}

export function CadragePanel({ matrix, company, role }: CadragePanelProps) {
  const queryClient = useQueryClient();
  const scrollerRef = useRef<HTMLDivElement>(null);
  const sujetId = useId();
  const contexteId = useId();

  const isAdmin = role === 'pms_admin';
  const locked = matrix.state === 'génération_en_cours';
  // « Nouvelle matrice depuis celle-ci », beside the frozen pivot and point of
  // view (issue #16): absent where the API would refuse it (409).
  const derive = useDeriveMatrix();
  const displayState = matrixDisplayState(matrix);
  const canDerive = displayState === 'terminee' || displayState === 'brouillon';
  const sections = useMemo(() => visibleCadrageSections(isAdmin), [isAdmin]);
  const sectionIds = useMemo(() => sections.map((section) => section.id), [sections]);
  const activeId = useSectionSpy(sectionIds, scrollerRef);

  // Deep link (`…?vue=cadrage#paliers`). Runs once per mount: it answers "where
  // did this link point", not "where is the reader now" — re-running it on a
  // later render would yank someone back up mid-scroll.
  // biome-ignore lint/correctness/useExhaustiveDependencies: deliberately mount-only; `isAdmin` is read at that moment, not watched.
  useEffect(() => {
    const id = sectionIdFromHash(window.location.hash, isAdmin);
    if (!id) return;
    scrollerRef.current?.querySelector(`#${CSS.escape(id)}`)?.scrollIntoView();
  }, []);

  function jumpTo(id: string) {
    scrollerRef.current
      ?.querySelector(`#${CSS.escape(id)}`)
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <div data-testid="cadrage-panel" className="flex min-h-0 flex-1 gap-8 overflow-hidden px-6">
      {/* The rail does not scroll — the column beside it is the single
          scroller, which is what keeps the six sections one continuous
          document instead of six panes. `self-start` + `sticky` so a rail that
          ever outgrows the viewport still behaves. */}
      <nav
        aria-label="Sections du cadrage"
        className="sticky top-0 flex w-56 shrink-0 flex-col gap-0 self-start py-6"
      >
        <p className="mb-2 font-bold text-caption text-gray-500 uppercase tracking-wider">
          Cadrage
        </p>
        {sections.map((section) => {
          const isActive = section.id === activeId;
          return (
            <button
              key={section.id}
              type="button"
              onClick={() => jumpTo(section.id)}
              aria-current={isActive ? 'true' : undefined}
              className={cn(
                'flex items-center gap-2 rounded-md border-l-[3px] px-3 py-2 text-left text-small',
                isActive
                  ? 'border-bleu-nuit bg-bleu-nuit-tint font-semibold text-bleu-nuit'
                  : 'border-transparent text-gray-600 hover:text-bleu-nuit',
              )}
            >
              <span
                aria-hidden="true"
                className={cn(
                  'size-2 shrink-0 rounded-full',
                  isActive ? 'bg-bleu-nuit' : 'bg-gray-100',
                )}
              />
              {section.label}
              {section.adminOnly && <Icon name="lock" size={14} />}
            </button>
          );
        })}

        {matrix.paliersFrozenAt && (
          <p className="mt-4 rounded-md bg-gray-50 p-3 text-caption text-gray-600 leading-relaxed">
            Paliers figés à la génération du{' '}
            <strong className="text-gray-900">{formatDate(matrix.paliersFrozenAt)}</strong>.
          </p>
        )}
      </nav>

      <div ref={scrollerRef} className="flex min-w-0 flex-1 flex-col gap-8 overflow-y-auto py-6">
        <div className="flex max-w-[59rem] flex-col gap-8">
          <SetupSection company={company} pivot={matrix.pivot} />
          <CompanyContexteSection company={company} />
          <PaliersSection matrix={matrix} company={company} locked={locked} />
          <SujetContexteSection
            matrix={matrix}
            locked={locked}
            sujetId={sujetId}
            contexteId={contexteId}
            onSaved={() => void queryClient.invalidateQueries({ queryKey: ['matrix', matrix.id] })}
            onNewFromThis={canDerive ? () => void derive.deriveMatrix(matrix.id) : undefined}
            newFromThisPending={derive.isPending}
            newFromThisError={derive.errorMessage}
          />

          <Section id="documents" title="Documents & digests">
            {/* Reused verbatim from the wizard (D23), as the drawer's Contexte
                tab did before it — one review surface, several homes, so a
                digest reviewed here and one reviewed at creation cannot drift
                apart. The persisted sujet/contexte are passed, never the
                drafts above: this surface saves explicitly, so what the server
                holds is what the last "Enregistrer" wrote, and an unsaved
                draft must not unlock a model call the server would refuse. */}
            <ContextFilesField matrixId={matrix.id} disabled={locked} />
            <div className="mt-6">
              <DigestReviewPanel
                matrixId={matrix.id}
                disabled={locked}
                sujet={matrix.sujet}
                contexte={matrix.contexte}
              />
            </div>
          </Section>

          {isAdmin && (
            <Section
              id="prompt"
              title="Prompt"
              badge={
                <span className="inline-flex items-center gap-1 rounded-md bg-bleu-nuit-tint px-2 py-0 font-semibold text-bleu-nuit text-caption">
                  <Icon name="lock" size={14} />
                  pms_admin
                </span>
              }
              description="Le texte exact envoyé au modèle, enregistré au lancement de chaque génération."
            >
              <CadragePromptSection matrixId={matrix.id} />
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Section chrome ───────────────────────────────────────────────────────────

interface SectionProps {
  id: string;
  title: string;
  badge?: ReactNode;
  /** Rendered top-right of the heading row — the "Modifier sur la fiche entreprise →" links. */
  action?: ReactNode;
  description?: string;
  children: ReactNode;
}

/** One `<section>` of the scroller. Its `id` is both the scroll-spy target and the `#deep-link`. */
function Section({ id, title, badge, action, description, children }: SectionProps) {
  return (
    // `scroll-mt-6` so a jump lands the heading below the scroller's top edge
    // rather than flush against it, where it reads as clipped.
    <section id={id} className="scroll-mt-6">
      <div className="flex flex-wrap items-baseline gap-3">
        <h2 className="font-semibold text-h4">{title}</h2>
        {badge}
        <div className="flex-1" />
        {action}
      </div>
      {description && <p className="mt-1 text-gray-600 text-small">{description}</p>}
      <div className="mt-4">{children}</div>
    </section>
  );
}

/**
 * The shared "this belongs to the company" marking. Both read-only sections
 * carry it, so the reason they cannot be edited here is stated where the
 * editing controls would otherwise be — not left to be inferred from their
 * absence.
 */
function ReadOnlyBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-0 text-caption text-gray-600">
      <Icon name="lock" size={14} />
      Lecture seule
    </span>
  );
}

function CompanySheetLink({ companyId }: { companyId: string }) {
  return (
    <Link
      to="/companies/$companyId"
      params={{ companyId }}
      className="font-semibold text-bleu-nuit text-small hover:underline"
    >
      Modifier sur la fiche entreprise →
    </Link>
  );
}

// ── 1. Setup de singularité (read-only) ──────────────────────────────────────

interface SetupSectionProps {
  company: CompanyWithSetup;
  /** The matrix's pivot: it titles each step, as at the wizard's step 1 (issue #16). */
  pivot: MatrixPivot;
}

function SetupSection({ company, pivot }: SetupSectionProps) {
  const ordered = [...company.singularitySetup].sort((a, b) => a.order - b.order);
  const [expanded, setExpanded] = useState<ReadonlySet<number>>(new Set());

  function handleExpandedChange(order: number, next: boolean) {
    setExpanded((previous) => {
      const updated = new Set(previous);
      if (next) updated.add(order);
      else updated.delete(order);
      return updated;
    });
  }

  return (
    <Section
      id="setup"
      title="Setup de singularité"
      badge={<ReadOnlyBadge />}
      action={<CompanySheetLink companyId={company.id} />}
      description={`Partagé par toutes les matrices de ${company.name} — une modification ici les affecterait toutes.`}
    >
      <div className="space-y-2">
        {ordered.map((step) => (
          <SetupStepRow
            key={step.order}
            pivot={pivot}
            step={step}
            expanded={expanded.has(step.order)}
            onExpandedChange={(next) => handleExpandedChange(step.order, next)}
          />
        ))}
      </div>
    </Section>
  );
}

// ── 2. Contexte de l'entreprise (read-only) ──────────────────────────────────

function CompanyContexteSection({ company }: { company: CompanyWithSetup }) {
  return (
    <Section
      id="contexte-entreprise"
      title="Contexte de l'entreprise"
      badge={<ReadOnlyBadge />}
      action={<CompanySheetLink companyId={company.id} />}
      description={`Partagé par toutes les matrices de ${company.name} — une modification ici les affecterait toutes.`}
    >
      <div className="rounded-md border border-gray-200 p-4">
        <p className="whitespace-pre-wrap text-small">
          {company.contexte?.trim() || 'Aucun contexte renseigné pour cette entreprise.'}
        </p>
        <p className="mt-3 text-caption text-gray-600">
          Noyau {company.noyau}
          {company.braavoLevel === null ? '' : ` · niveau Braavo ${company.braavoLevel}`}
        </p>
      </div>
    </Section>
  );
}

// ── 3. Paliers ───────────────────────────────────────────────────────────────

interface PaliersSectionProps {
  matrix: MatrixWithDimensions;
  company: CompanyWithSetup;
  locked: boolean;
}

function PaliersSection({ matrix, company, locked }: PaliersSectionProps) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ResolvedPalier[]>(matrix.paliers);
  const [saved, setSaved] = useState<ResolvedPalier[]>(matrix.paliers);

  /**
   * The level "personnalisé" and "reset" resolve against — and it is not the
   * same level before and after a generation (see `getResolvedPaliers`).
   *
   * While `paliersFrozenAt` is null the matrix tracks its company live, so the
   * company's resolved table is the fallback. Once frozen, the company level is
   * skipped entirely and the fallback is the global default. Handing the
   * editor the company table on a frozen matrix would mark the wrong paliers as
   * overridden and make "reset" restore text the prompt would never use.
   */
  const frozen = matrix.paliersFrozenAt !== null;
  const inherited = useMemo<ResolvedPalier[]>(
    () =>
      frozen
        ? company.palierDefaults.map((fallback) => ({
            number: fallback.number,
            label: fallback.defaultLabel,
            description: fallback.defaultDescription,
          }))
        : company.paliers,
    [frozen, company.palierDefaults, company.paliers],
  );

  const isDirty = draft.some((palier) => {
    const previous = saved.find((entry) => entry.number === palier.number);
    return (
      palier.label !== previous?.label ||
      (palier.description ?? null) !== (previous?.description ?? null)
    );
  });

  const saveMutation = useMutation({
    mutationFn: () =>
      matricesApi.updatePaliers(
        matrix.id,
        draft.map((palier) => {
          const base = inherited.find((entry) => entry.number === palier.number);
          // A value equal to the inherited one persists as `null`, not as a
          // copy: that is what "reprendre la valeur de l'entreprise" means, and
          // storing the copy would silently pin the palier to today's wording
          // the next time the level below changes.
          return {
            number: palier.number,
            customLabel: palier.label === base?.label ? null : palier.label,
            customDescription:
              (palier.description ?? null) === (base?.description ?? null)
                ? null
                : palier.description,
          };
        }),
      ),
    onSuccess: () => {
      setSaved(draft);
      // On a `généré` matrix this write moved `contextUpdatedAt` server-side,
      // which is what raises the staleness banner and the per-cell markers.
      void queryClient.invalidateQueries({ queryKey: ['matrix', matrix.id] });
    },
  });

  return (
    <Section
      id="paliers"
      title="Paliers"
      badge={
        <span className="rounded-md bg-gray-100 px-2 py-0 text-caption text-gray-600">
          Propres à cette matrice
        </span>
      }
      description="Le libellé est l’en-tête de colonne ; la description est la définition de référence du palier fournie au modèle. Enregistrer marque les cellules générées avant ce changement comme potentiellement dépassées."
    >
      <PaliersEditor
        paliers={draft}
        inherited={inherited}
        resetLabel={
          frozen ? 'Rétablir la valeur par défaut' : "Reprendre la valeur de l'entreprise"
        }
        overriddenLegend="personnalisé pour cette matrice"
        disabled={locked}
        onChange={setDraft}
      />

      <div className="mt-4 flex items-center gap-3">
        <Button
          variant="secondary"
          type="button"
          onClick={() => saveMutation.mutate()}
          disabled={!isDirty || locked || saveMutation.isPending}
        >
          {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer les paliers'}
        </Button>
        {saveMutation.isSuccess && !isDirty && (
          <output className="text-small text-success">Paliers enregistrés.</output>
        )}
      </div>
      {saveMutation.isError && <Alert tone="danger">{saveErrorMessage(saveMutation.error)}</Alert>}
    </Section>
  );
}

// ── 4. Sujet & contexte ──────────────────────────────────────────────────────

interface SujetContexteSectionProps {
  matrix: MatrixWithDimensions;
  locked: boolean;
  sujetId: string;
  contexteId: string;
  onSaved: () => void;
  onNewFromThis: (() => void) | undefined;
  newFromThisPending: boolean;
  newFromThisError: string | null;
}

/**
 * What `ContexteTab` used to do, moved here unchanged in substance (D21/D22).
 *
 * Explicit save, never a debounce. Every write moves both `contextUpdatedAt`
 * (which marks generated cells stale and re-raises the banner) and
 * `framingUpdatedAt` (which dates the digests) — doing that on a keystroke
 * timer would flag the matrix repeatedly while someone is still mid-sentence.
 * On a `généré` matrix, re-framing is a deliberate act and is treated as one.
 */
function SujetContexteSection({
  matrix,
  locked,
  sujetId,
  contexteId,
  onSaved,
  onNewFromThis,
  newFromThisPending,
  newFromThisError,
}: SujetContexteSectionProps) {
  const [sujetDraft, setSujetDraft] = useState(matrix.sujet ?? '');
  const [contexteDraft, setContexteDraft] = useState(matrix.contexte ?? '');

  const saveMutation = useMutation({
    mutationFn: () =>
      matricesApi.patchContext(matrix.id, { sujet: sujetDraft, contexte: contexteDraft }),
    onSuccess: onSaved,
  });

  const trimmedSujet = sujetDraft.trim();
  const trimmedContexte = contexteDraft.trim();
  const isDirty = sujetDraft !== (matrix.sujet ?? '') || contexteDraft !== (matrix.contexte ?? '');
  const canSave =
    trimmedSujet.length > 0 &&
    trimmedContexte.length > 0 &&
    isDirty &&
    !locked &&
    !saveMutation.isPending;

  return (
    <Section
      id="sujet-contexte"
      title="Sujet & contexte"
      description="Ce qui alimente le prompt de génération. Toute modification marque les cellules générées avant ce changement comme potentiellement dépassées — et signale les digests produits sous l’ancien cadrage."
    >
      <div className="space-y-4">
        {/* Kept apart from the editable fields below and from their
            « Enregistrer »: the pivot and point of view never ride on this
            section's save (issue #16, planche 07). */}
        <CadrageFrozenChoices
          pivot={matrix.pivot}
          destinataire={matrix.destinataire}
          destinataireAutre={matrix.destinataireAutre}
          onNewFromThis={onNewFromThis}
          newFromThisPending={newFromThisPending}
          newFromThisError={newFromThisError}
        />
        <hr className="h-0.5 border-0 bg-gray-100" />

        <div>
          <TextArea
            label="Sujet"
            id={sujetId}
            status={trimmedSujet.length === 0 ? 'error' : 'default'}
            value={sujetDraft}
            onChange={(event) => setSujetDraft(event.target.value)}
            disabled={locked}
            aria-required="true"
            aria-invalid={trimmedSujet.length === 0}
          />
          {trimmedSujet.length === 0 && <Alert tone="danger">Le sujet est requis.</Alert>}
        </div>

        <div>
          <TextArea
            label="Contexte"
            id={contexteId}
            status={trimmedContexte.length === 0 ? 'error' : 'default'}
            value={contexteDraft}
            onChange={(event) => setContexteDraft(event.target.value)}
            disabled={locked}
            aria-required="true"
            aria-invalid={trimmedContexte.length === 0}
          />
          {trimmedContexte.length === 0 && <Alert tone="danger">Le contexte est requis.</Alert>}
        </div>

        {saveMutation.isError && (
          <Alert tone="danger">{saveErrorMessage(saveMutation.error)}</Alert>
        )}

        <div className="flex items-center gap-3">
          <Button
            variant="secondary"
            type="button"
            onClick={() => saveMutation.mutate()}
            disabled={!canSave}
          >
            {saveMutation.isPending ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
          {/* `FieldMessage` only carries danger/warning — a save confirmation
              is neither, so it gets its own quiet status line. `<output>`
              rather than a `role="status"` div: it carries that role
              implicitly, which is what Biome's `useSemanticElements` asks. */}
          {saveMutation.isSuccess && !isDirty && (
            <output className="text-small text-success">Cadrage enregistré.</output>
          )}
          {!isDirty && !saveMutation.isSuccess && (
            <span className="text-gray-500 text-small">Aucune modification à enregistrer.</span>
          )}
        </div>
      </div>
    </Section>
  );
}
